import React, { useState, useEffect, useMemo, useRef } from "react";
import { api, serverUrl } from "../../../Requests/Api";
import { useAppContext } from "../../../State/Global/AppContext";
import { useTranslation } from "react-i18next";
import { PageHeader, message, Button, notification, Tag, Tooltip } from "antd";
import moment from "moment";
import CTA from "../../../Constants";
import SendIcon from "Img/send.svg";
import Chat from "frontend-lib/Chat/Chat";
import { ReloadOutlined, CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";
import EmptyChat from "../EmptyChat/EmptyChat";
import UploadMediaModal from "../UploadMediaModal/UploadMediaModal";
import NotesModal from "../NotesModal/NotesModal";
import CharSizeWarning from "../CharSizeWarning/CharSizeWarning";

import { messageParser, ownerParser, getCustomerName } from "../ChatUtils/utils";
import {
    allowedImageMIMETypes,
    allowedVideoMIMETypes,
    allowedAudioMIMETypes,
    allowedDocMIMETypes,
    fileExceedsLimit,
    fileIsInAllowedTypes,
} from "Components/ChatHandler/ChatUtils/fileUtils";

import styles from "./ChatHandler.module.scss";
import { baseStyles } from "./baseStyles";
import "frontend-lib/Chat/Chat.scss";
import "./ChatStyles.scss";
import { Logger } from "frontend-lib/Log";
import ChatHandlerButtonsNative from "./ChatHandlerButtonsNative";
import ChatNotFound from "../ChatNotFound/ChatNotFound";

function generateRandomString(n) {
    const s = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    return Array(n)
        .join()
        .split(",")
        .map(() => {
            return s.charAt(Math.floor(Math.random() * s.length));
        })
        .join("");
}

const { assignButton } = styles;

function ChatHandler({
    className,
    active,
    closeChatCb,
    isMobile,
    chatSocket,
    openInfoCb,
    height,
    handleOpenPredictModal,
}) {
    const { t } = useTranslation();
    let placeHolderMessages = {
        error: t("chatPlaceholders.error"),
        connected: t("chatPlaceholders.connected"),
        loading: t("chatPlaceholders.loading"),
        assign: t("chatPlaceholders.assign"),
        unavailable: t("chatPlaceholders.unavailable"),
        otherAgent: t("chatPlaceholders.otherAgent"),
        ended: t("chatPlaceholders.ended"),
        waiting: t("chatPlaceholders.waiting"),
        reconnecting: t("chatPlaceholders.reconnecting"),
        disconnected: t("chatPlaceholders.disconnected"),
    };
    const { actions, appState } = useAppContext();
    const [lang] = useState("ENGLISH");
    const [configs] = useState({ customStyles: baseStyles });
    const [messages, setMessages] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [isChatEnabled, setIsChatEnabled] = useState(true);
    const [chatState, setChatState] = useState(CTA.CHATS.STATES.EMPTY);
    const chatStateRef = useRef(chatState);
    const [scrollOnNewMessages, setScrollOnNewMessages] = useState(true);
    const [showAssignButton, setShowAssignButton] = useState(false);
    const [confirmModal, setConfirmModal] = useState(false);
    const [attachment, seAttachment] = useState(null);
    const [previewImageUrl, setPreviewImageUrl] = useState(null);
    const [originalFileName, setOriginalFileName] = useState();
    const [textInputValue, setTextInputValue] = useState("");
    const [isNotesModalVisible, setIsNotesModalVisible] = useState(false);
    const [isModalNoteLoading, setIsModalNoteLoading] = useState(false);
    const [lastTextSended, setLastTextSended] = useState(null);
    const [inputPlaceholder, setInputPlaceholder] = useState(placeHolderMessages.loading);
    const [paginator, setPaginator] = useState({
        page: 0,
        hasNext: false,
    });

    const headerRef = useRef();
    const selectedChatRef = useRef(appState.selectedChat);

    const selectedChatId = useMemo(() => {
        return appState.selectedChat.id;
    }, [appState.selectedChat.id]);

    const chatOwner = useMemo(() => {
        return appState.selectedChat.ownedBy?.id;
    }, [appState.selectedChat.ownedBy?.id]);

    const chatStatus = useMemo(() => {
        return appState.selectedChat.status;
    }, [appState.selectedChat.status]);

    const currentChatOption = useMemo(() => {
        if (appState.currentChatOption.selectedChatId) {
            return appState.currentChatOption;
        }
    }, [appState.currentChatOption.selectedChatId]);

    const logger = new Logger("ChatHandler Agent", serverUrl);

    const chatChannel = useMemo(() => {
        return appState.selectedChat.customerChannel?.type;
    }, [appState.selectedChat.customerChannel?.type]);

    const showAudioRecorderButton = useMemo(() => {
        if (chatChannel === "FACEBOOK_COMMENT") {
            return false;
        }
        return appState.workspaceConfig?.agent?.showAudioRecorderButton || false;
    }, [appState.workspaceConfig?.agent?.showAudioRecorderButton, chatChannel]);

    const showSendMultimediaButton = useMemo(() => {
        if (chatChannel === "FACEBOOK_COMMENT") {
            return false;
        }
        return true;
    }, [chatChannel]);

    const chatSocketStatus = useMemo(() => {
        return appState.chatSocketStatus;
    }, [appState.chatSocketStatus]);

    const currentAgentId = useMemo(() => {
        return appState.agent?.id;
    }, [appState.agent?.id]);

    const chatIsFromComment = useMemo(() => {
        return [CTA.CHANNELS.FACEBOOK_COMMENT].includes(chatChannel);
    }, [chatChannel]);

    const [ionSafeAreasPx, _] = useState(() => {
        const root = document.documentElement;

        const ionSafeAreaBottom = getComputedStyle(root)
            .getPropertyValue("--ion-safe-area-bottom")
            .trim()
            .replace("px", "");
        const ionSafeAreaTop = getComputedStyle(root)
            .getPropertyValue("--ion-safe-area-top")
            .trim()
            .replace("px", "");

        return {
            bottom: ionSafeAreaBottom ? Number(ionSafeAreaBottom) : 0,
            top: ionSafeAreaTop ? Number(ionSafeAreaTop) : 0,
        };
    }, []);

    /**
     * @type  {{set: boolean, chatId: number} | undefined}
     */
    const deepLinkChatState = useMemo(
        () => appState.deepLinkChatNotFound,
        [appState.deepLinkChatNotFound]
    );

    async function logout() {
        const loaderHash = actions.setLoader();
        try {
            await api.auth().signOut();
            actions.unsetLoader(loaderHash);
            actions.setAuth(false);
        } catch (err) {
            actions.unsetLoader(loaderHash);
            console.log(err);
        }
    }

    function parseMessages(messages) {
        const uiMessages = [];
        for (let i = messages.length - 1; i >= 0; i--) {
            let message = messages[i];
            let uiMsg = messageParser(message, selectedChatRef);
            if (uiMsg.messageFrom !== null) {
                uiMessages.push(uiMsg);
            }
        }
        return uiMessages;
    }

    function handleInteraction(interaction, payload) {
        switch (interaction) {
            case CTA.CHATS.CONTENT_TYPE.OPTIONS:
                handleOptions(payload);
                break;
            case CTA.CHATS.CONTENT_TYPE.MAP:
                handleLocation(payload);
                break;
            case CTA.CHATS.CONTENT_TYPE.ITEMS_CAROUSEL_MESSAGE:
                handleCarrouselItem(payload);
                break;
            default:
                console.log("Not implemented");
        }
    }

    function handleOptions(payload) {
        throw new Error("Options handler not implenmented");
    }

    function handleLocation(payload) {
        throw new Error("Location handler not implenmented");
    }

    function handleFileUpload(file, fileName, preview) {
        throw new Error("File uploader handler not implemented");
    }

    function handleCarrouselItem(payload) {
        throw new Error("Carrousel handler not implemented");
    }

    async function emitMessage(event, msg) {
        return new Promise((resolve, reject) => {
            chatSocket.emit(event, msg, (res) =>
                res && res.status === "OK" ? resolve() : reject()
            );
        });
    }

    function addRandomToFileName(file) {
        const splitResult = file.name.split(".");
        const extension = splitResult.pop(-1);
        const fileName = splitResult.join(".");

        const newFileName = `${fileName}_${generateRandomString(8)}.${extension}`;

        const blob = file.slice(0, file.size, file.type);
        const renamedFile = new File([blob], newFileName, { type: file.type });
        return renamedFile;
    }

    const handleMultimediaPreview = (file) => {
        if (file && fileExceedsLimit(file.size)) {
            notification["error"]({
                message: "Error",
                description: t("fileUpload.maxSize"),
                duration: 4,
            });
            return;
        }

        if (!fileIsInAllowedTypes(file.type)) {
            notification["error"]({
                message: "Error",
                description: t("fileUpload.notAllowed"),
                duration: 0,
            });
            return;
        }
        setOriginalFileName(file.name);
        file = addRandomToFileName(file);
        const reader = new FileReader();
        reader.onload = function (e) {
            if (isFileImage(file)) {
                const img = document.createElement("img");
                img.src = e.target.result;
                setPreviewImageUrl(img.src);
            } else {
                setPreviewImageUrl(null);
            }
            setConfirmModal(true);
            seAttachment(file);
        };
        reader.readAsDataURL(file);
    };

    async function handleMultimediaMessage(file) {
        const loaderHash = actions.setLoader();
        try {
            let multimediaType;
            let text = file.name;
            const formData = new FormData();
            formData.append("file", file);
            formData.append("useAbsoluteUrl", true);

            if (file.size >= 50 * 1000 * 1000) {
                notification["error"]({
                    message: "Error",
                    description: t("fileUpload.maxSize"),
                    duration: 4,
                });
                return;
            }

            if (allowedImageMIMETypes.includes(file.type)) {
                multimediaType = CTA.CHATS.CONTENT_TYPE.IMAGE;
            } else if (allowedVideoMIMETypes.includes(file.type)) {
                multimediaType = CTA.CHATS.CONTENT_TYPE.VIDEO;
            } else if (allowedAudioMIMETypes.includes(file.type)) {
                multimediaType = CTA.CHATS.CONTENT_TYPE.AUDIO;
            } else if (allowedDocMIMETypes.includes(file.type)) {
                multimediaType = CTA.CHATS.CONTENT_TYPE.DOCUMENT;
                text = file.name;
            } else {
                notification["error"]({
                    message: "Error",
                    description: t("fileUpload.notAllowed"),
                    duration: 4,
                });
                return;
            }

            const res = await api.chats().upload(formData);
            let fileName = res.data.url;
            const data = {
                type: multimediaType,
                text: text,
                fileName: fileName,
            };

            await emitMessage("client_message", data);
            const newMessage = {
                userType: CTA.CHATS.USER_TYPE.AGENT,
                contentType: multimediaType,
                text: text,
                owner: ownerParser(message, selectedChatRef),
                fileName: fileName,
                createdOn: moment().local().format("HH:mm"),
                id: moment().local().format() + selectedChatRef.id + Math.random(),
            };
            setMessages((oldMessages) => [...oldMessages, newMessage]);
            actions.unsetLoader(loaderHash);
        } catch (err) {
            actions.unsetLoader(loaderHash);
            console.log(err);
        }
    }

    function handleNewServerMessage(msg) {
        let newServerMessage = messageParser(msg, selectedChatRef);
        if (chatStateRef.current === CTA.CHATS.STATES.OPEN) {
            setMessages((oldMessages) => [...oldMessages, newServerMessage]);
        }
    }

    async function startConversationFromComment(channel, textInputValue) {
        const selectedChatUserUuid = selectedChatRef.current.userUuid;
        const loaderHash = actions.setLoader();

        switch (channel) {
            case CTA.CHANNELS.FACEBOOK_COMMENT:
                try {
                    await api.chats().startFromFbComment(selectedChatUserUuid, textInputValue);
                    message.success(t("startConversation.messageSent"), 4);
                    closeChatCb();
                } catch (err) {
                    const responseStatusCode = err.response?.status;
                    const responseErrorKey = err.response?.data?.error?.key;
                    if (responseStatusCode === 400) {
                        if (responseErrorKey === "CONVERSATION_BLOCKED") {
                            message.error(t("startConversation.CONVERSATION_BLOCKED"));
                            return;
                        } else if (
                            responseErrorKey === "CANNOT_START_CONVERSATION_ON_DELETED_COMMENT"
                        ) {
                            message.error(
                                t("startConversation.CANNOT_START_CONVERSATION_ON_DELETED_COMMENT")
                            );
                            return;
                        }
                    }
                    message.error(t("serverError"));
                    logger.error(
                        `Error in startConversationFromComment with channel ${channel} and selectedChatUserUuid ${selectedChatUserUuid}. Error is: ${err}`
                    );
                    return;
                } finally {
                    setTextInputValue("");
                    actions.unsetLoader(loaderHash);
                }
                return;
            default:
                throw Error(`startConversationFromComment in channel ${channel} not implemented`);
        }
    }

    async function handleNewClientMessage() {
        if (textInputValue === lastTextSended) {
            setTextInputValue("");
            return false;
        }
        if (chatIsFromComment) {
            startConversationFromComment(chatChannel, textInputValue);
            return;
        }

        const loaderHash = actions.setLoader();
        try {
            const socketMessage = {
                text: textInputValue,
                type: CTA.CHATS.CONTENT_TYPE.TEXT,
            };
            const getAgentName = () => {
                if (appState.agent.firstName && appState.agent.lastName) {
                    return appState.agent.firstName + " " + appState.agent.lastName;
                } else {
                    return appState.agent.email;
                }
            };
            const uiMessage = {
                userType: CTA.CHATS.USER_TYPE.AGENT,
                contentType: CTA.CHATS.CONTENT_TYPE.PLAIN,
                text: textInputValue,
                owner: getAgentName(),
                attachment: null,
                createdOn: moment().local().format("HH:mm"),
                id: moment().local().format() + selectedChatRef.id + Math.random(),
            };
            await emitMessage("client_message", socketMessage);
            setLastTextSended(textInputValue);
            setTextInputValue("");
            updateSeen(appState.selectedChat);
            setMessages((oldMessages) => [...oldMessages, uiMessage]);
            actions.unsetLoader(loaderHash);
        } catch (error) {
            actions.setLoader();
            notification["error"]({
                message: "Error",
                description: t("chatSocketError"),
                duration: 0,
            });
            setTextInputValue("");
        }
    }

    function handleChatTextInput(selected) {
        if (selected.status === CTA.FILTERS.ENDED) {
            setInputPlaceholder(placeHolderMessages.ended);
            setIsChatEnabled(false);
            setIsConnected(false);
        } else if (selected.status === CTA.FILTERS.INIT_BY_AGENT) {
            setInputPlaceholder(placeHolderMessages.waiting);
            setIsChatEnabled(false);
            setIsConnected(false);
        } else if (selected.ownedBy?.id === currentAgentId) {
            setInputPlaceholder(placeHolderMessages.connected);
            setIsChatEnabled(true);
            setIsConnected(true);
        } else if (selected.ownedBy) {
            setInputPlaceholder(placeHolderMessages.otherAgent);
            setIsChatEnabled(false);
        } else if (chatChannel === CTA.CHANNELS.FACEBOOK_COMMENT) {
            setIsChatEnabled(true);
            setIsConnected(true);
        } else {
            setInputPlaceholder(placeHolderMessages.assign);
            setIsChatEnabled(false);
            setIsConnected(true);
        }
    }

    function updateSeen(selected) {
        if (selected.amountNew > 0) {
            actions.updateChatSeen(selected);
        }
    }

    function getHistory(chat, page) {
        if (appState.chatsListFilters?.status === CTA.FILTERS.ENDED) {
            return api.chats().getHistory(page, undefined, chat.id, false);
        } else {
            return api.chats().getHistory(page, chat.userUuid);
        }
    }

    async function getHistoryAndJoin(selected) {
        setChatState(CTA.CHATS.STATES.LOADING);
        setLastTextSended(null);
        const loaderHash = actions.setLoader();
        try {
            const resChathistory = await getHistory(selected, 0);
            if (chatSocketStatus !== CTA.SOCKET_STATUS.CONNECTED) {
                notification["warning"]({
                    message: t("connection.reconnectingTitle"),
                    description: t("connection.reconnectingDesc"),
                    duration: 6,
                });
                const chatHistoryMessages = parseMessages(resChathistory.data.chatHistory);
                setMessages([]);
                setMessages([...chatHistoryMessages]);
                actions.unsetLoader(loaderHash);
                return;
            }

            await emitMessage("join_room", { user_uuid: selected.userUuid });

            if (selected.id !== selectedChatId) {
                return;
            }

            const chatHistoryMessages = parseMessages(resChathistory.data.chatHistory);
            setMessages([]);
            setMessages([...chatHistoryMessages]);
            setPaginator(resChathistory.data.paginator);
            setChatState(CTA.CHATS.STATES.OPEN);
            handleChatTextInput(selected);
            actions.unsetLoader(loaderHash);
        } catch (err) {
            setInputPlaceholder(placeHolderMessages.error);
            setChatState(CTA.CHATS.STATES.ERROR);
            notification["error"]({
                message: "Error",
                description: t("chatSocketError"),
                duration: 0,
            });
            actions.setSelectedChat({});
            actions.unsetLoader(loaderHash);
        }
    }

    async function reloadHistory(selected) {
        setChatState(CTA.CHATS.STATES.LOADING);
        const loaderHash = actions.setLoader();
        try {
            const resChathistory = await getHistory(selected, 0);
            if (selected.id !== selectedChatId) {
                return;
            }
            const chatHistoryMessages = parseMessages(resChathistory.data.chatHistory);
            setMessages([...chatHistoryMessages]);
            setPaginator(resChathistory.data.paginator);
            setChatState(CTA.CHATS.STATES.OPEN);
            handleChatTextInput(selected);
            actions.setLikedAnswer(null);
            actions.unsetLoader(loaderHash);
        } catch (err) {
            setInputPlaceholder(placeHolderMessages.error);
            setChatState(CTA.CHATS.STATES.ERROR);
            notification["error"]({
                message: "Error",
                description: t("chatSocketError"),
                duration: 0,
            });
            actions.setLikedAnswer(null);
            actions.unsetLoader(loaderHash);
            console.log(err);
        }
    }

    const onClickLoadMore = () => {
        setScrollOnNewMessages(false);
    };

    const loadMoreHistory = async () => {
        const loaderHash = actions.setLoader();
        try {
            const response = await getHistory(appState.selectedChat, paginator.page + 1);
            const historyMessages = parseMessages(response.data.chatHistory);
            setMessages((prevState) => [...historyMessages, ...prevState]);
            setPaginator(response.data.paginator);
            actions.unsetLoader(loaderHash);
            setScrollOnNewMessages(true);
        } catch (err) {
            actions.unsetLoader(loaderHash);
            console.log(err);
        }
    };

    const handleReferencedClick = async (referencedClicked) => {
        let finalMessagesToShow = [...messages];
        let lastPaginator = null;
        const loaderHash = actions.setLoader();
        let referencedMsg = document.getElementById("message-" + referencedClicked.id);
        if (referencedMsg) {
            referencedMsg.scrollIntoView();
            actions.unsetLoader(loaderHash);
            return;
        }
        const maxIterations = 10;
        let referenced = [];
        for (let i = 0; i < maxIterations; i++) {
            const page = i === 0 ? paginator.page : lastPaginator.page;
            if (!paginator.hasNext) {
                break;
            }
            const response = await getHistory(appState.selectedChat, page + 1);
            const historyMessages = parseMessages(response.data.chatHistory);
            finalMessagesToShow = [...historyMessages, ...finalMessagesToShow];
            lastPaginator = response.data.paginator;
            referenced = historyMessages.filter((message) => message.id === referencedClicked.id);
            if (referenced.length > 0) {
                break;
            }
        }
        if (referenced.length > 0) {
            setMessages(finalMessagesToShow);
            setPaginator(lastPaginator);
            referencedMsg = document.getElementById("message-" + referencedClicked.id);
            referencedMsg.scrollIntoView();
        } else {
            message.error("No se encontró el mensaje");
        }
        actions.unsetLoader(loaderHash);
    };

    const dragOver = (e) => {
        e.preventDefault();
    };

    const dragEnter = (e) => {
        e.preventDefault();
    };

    const dragLeave = (e) => {
        e.preventDefault();
    };

    function isFileImage(file) {
        return file && file["type"].split("/")[0] === "image";
    }

    const fileDrop = (e) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (files.length) {
            handleMultimediaPreview(files[0]);
        }
    };

    const handleModalOk = () => {
        handleMultimediaMessage(attachment);
        seAttachment(false);
        setConfirmModal(false);
        setOriginalFileName();
    };

    const handleModalCancel = () => {
        setConfirmModal(false);
        seAttachment(false);
        setOriginalFileName();
    };

    async function handleChatOptions(chat, owner, action) {
        const loaderHash = actions.setLoader();
        try {
            if (action === CTA.CHATS.OPTIONS.ASSIGN || action === CTA.CHATS.OPTIONS.UNASSIGN) {
                await api.chats().updateChat(chat.id, { markAsOwnedBy: owner });
                actions.setChatOwner(chat, owner);
                if (action === CTA.CHATS.OPTIONS.ASSIGN) {
                    const selected = {
                        ...chat,
                        ownedBy: {
                            ...chat.ownedBy,
                            isCurrentUser: true,
                        },
                    };
                    actions.setSelectedChat(selected);
                    getHistoryAndJoin(selected);
                }
                if (
                    action === CTA.CHATS.OPTIONS.UNASSIGN &&
                    chat.id === appState.selectedChat?.id
                ) {
                    setChatState(CTA.CHATS.STATES.EMPTY);
                    actions.setSelectedChat({});
                    closeChatCb();
                }
                actions.unsetLoader(loaderHash);
            }
            if (action === CTA.CHATS.OPTIONS.CLOSE) {
                await api.chats().close(chat.id);
                actions.setClosedChat(chat);
                if (chat.id === appState.selectedChat?.id) {
                    actions.setSelectedChat({});
                }
                actions.unsetLoader(loaderHash);
            }
        } catch (err) {
            actions.unsetLoader(loaderHash);
            console.log(err);
        }
    }

    function handleAssignButton() {
        actions.setChatOwnerButton(true);
    }

    function handleTextInputChange(val) {
        setTextInputValue(val);
    }

    function deselectChat() {
        closeChatCb();
    }

    function handleNote() {
        setIsNotesModalVisible(true);
    }

    function handleNewAgentNote(note) {
        const noteMessage = {
            text: note,
            messagePayload: {
                text: note,
                type: "TEXT",
            },
            messageFrom: "ASSISTANT_NOTE",
            userUuid: appState.selectedChat.userUuid,
            createdOn: moment().local().format("HH:mm"),
            id: moment().local().format() + selectedChatRef.id + Math.random(),
        };
        setIsModalNoteLoading(true);
        api.chats()
            .sendNote(noteMessage)
            .then((res) => {
                const loaderHash = actions.setLoader();
                setMessages((oldMessages) => [...oldMessages, noteMessage]);
                actions.unsetLoader(loaderHash);
                setIsNotesModalVisible(false);
                setIsModalNoteLoading(false);
            })
            .catch((err) => {
                setIsModalNoteLoading(false);
                setIsNotesModalVisible(false);
            });
    }

    function getHeaderHeight() {
        return headerRef.current ? headerRef.current.clientHeight : 0;
    }

    function isFbCommentAndDeleted() {
        const lastMessage = messages && messages.length && messages.at(-1);
        const deletedOn = lastMessage && lastMessage.deletedOn;

        if (deletedOn && chatChannel === "FACEBOOK_COMMENT") {
            return true;
        }
        return false;
    }

    useEffect(() => {
        if (!isChatEnabled) return;
        if (isFbCommentAndDeleted()) {
            setIsChatEnabled(false);
            return;
        }
    }, [isChatEnabled]);

    useEffect(() => {
        if (messages.length > 0) {
            actions.setSelectedChatHistory(messages);
        }
        // eslint-disable-next-line
    }, [messages]);

    useEffect(() => {
        if (!scrollOnNewMessages) {
            loadMoreHistory();
        }
        // eslint-disable-next-line
    }, [scrollOnNewMessages]);

    useEffect(() => {
        if (appState.likedAnswer) {
            reloadHistory(appState.selectedChat);
        }
        // eslint-disable-next-line
    }, [appState.likedAnswer]);

    useEffect(() => {
        selectedChatRef.current = appState.selectedChat;
        if (
            Object.keys(appState.selectedChat).length !== 0 &&
            appState.selectedChat?.constructor === Object
        ) {
            if (appState.selectedChat.ownedBy?.isCurrentUser) {
                setShowAssignButton(true);
            } else {
                setShowAssignButton(false);
            }
            getHistoryAndJoin(appState.selectedChat);
        }
    }, [selectedChatId]);

    useEffect(() => {
        if (
            currentChatOption &&
            currentChatOption.action === CTA.CHATS.OPTIONS.RELOAD &&
            currentChatOption.selectedChatId === appState.selectedChat.id
        ) {
            actions.setCurrentChatOption(null, null);
            getHistoryAndJoin(appState.selectedChat);
        }
    }, [currentChatOption]);

    useEffect(() => {
        chatStateRef.current = chatState;
        if (isFbCommentAndDeleted()) {
            setIsChatEnabled(false);
        }
    }, [chatState]);

    useEffect(() => {
        if (!chatSocketStatus) return;
        if (
            chatSocketStatus === CTA.SOCKET_STATUS.RECONNECTING ||
            chatSocketStatus === CTA.SOCKET_STATUS.ERROR
        ) {
            setInputPlaceholder(placeHolderMessages.reconnecting);
            setIsChatEnabled(false);
            setIsConnected(false);
        }
        if (chatSocketStatus === CTA.SOCKET_STATUS.CONNECTED) {
            setInputPlaceholder(placeHolderMessages.connected);
            setIsChatEnabled(true);
            setIsConnected(true);
            if (appState.selectedChat.id) {
                getHistoryAndJoin(appState.selectedChat);
            }
        }
        if (chatSocketStatus === CTA.SOCKET_STATUS.DISCONNECTED) {
            setInputPlaceholder(placeHolderMessages.disconnected);
            setIsChatEnabled(false);
            setIsConnected(false);
        }
    }, [chatSocketStatus]);

    useEffect(() => {
        if (chatSocket) {
            chatSocket.on("server_message", handleNewServerMessage);
        }
        // eslint-disable-next-line
    }, [chatSocket]);

    function renderAssignButton() {
        const chatIsEnded = chatStatus === CTA.FILTERS.ENDED;

        const canBeAssigned = !chatOwner && !chatIsEnded && !chatIsFromComment;

        if (canBeAssigned) {
            return (
                <button className={assignButton} onClick={handleAssignButton}>
                    {t("chatOptions.assign")}
                </button>
            );
        }
        return <></>;
    }

    useEffect(() => {
        document.onpaste = function (evt) {
            const dT = evt.clipboardData || window.clipboardData;
            const file = dT.files[0];
            const newFile = new File([file], `${Date.now()}${file?.name}`, {
                type: file?.type,
            });
            if (selectedChatRef.current?.id && file && isFileImage(file)) {
                let reader = new FileReader();
                reader.onload = function (e) {
                    let img = document.createElement("img");
                    img.src = e.target.result;
                    setPreviewImageUrl(img.src);
                    setConfirmModal(true);
                    seAttachment(newFile);
                };
                reader.readAsDataURL(file);
            }
        };
    }, []);

    if (appState.selectedChat?.id) {
        return (
            <section
                onDragOver={dragOver}
                onDragEnter={dragEnter}
                onDragLeave={dragLeave}
                onDrop={fileDrop}
                className={`${className.split(",")?.map((item) => item)} agent-chat`}
                style={{ zIndex: active ? 1 : 0 }}
            >
                <UploadMediaModal
                    attachment={attachment}
                    previewImageUrl={previewImageUrl}
                    handleModalOkCb={handleModalOk}
                    handleModalCancelCb={handleModalCancel}
                    isVisible={confirmModal}
                    chatChannel={chatChannel}
                    originalFileName={originalFileName}
                />
                <NotesModal
                    isVisible={isNotesModalVisible}
                    isLoading={isModalNoteLoading}
                    handleNewAgentNoteCb={handleNewAgentNote}
                    handleCancelNotesModalCb={() => setIsNotesModalVisible(false)}
                />
                <header ref={headerRef} className={"opt-chat-header"}>
                    <PageHeader
                        key="chatHandlerHeader"
                        subTitle={
                            <div className={"chat-header"}>
                                <span
                                    style={{
                                        maxWWidth: "58%",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                    key="customerHeaderName"
                                >
                                    {getCustomerName(appState.selectedChat?.customer)}
                                </span>
                                {appState.selectedChat.groups.length > 0 &&
                                    appState.selectedChat.groups?.map((group) => (
                                        <Tag>{group.name}</Tag>
                                    ))}
                                {!isMobile && (
                                    <div
                                        style={{
                                            marginLeft: "auto",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {/* TODO: cambiar esto a un memo */}
                                        {appState.selectedChat.ownedBy?.id ===
                                            appState.agent.id && (
                                            <Button
                                                style={{ marginRight: "5px", fontSize: "13px" }}
                                                onClick={handleNote}
                                            >
                                                <PlusCircleOutlined />
                                                {t("notes.create")}
                                            </Button>
                                        )}

                                        <Tooltip
                                            placement="topLeft"
                                            title={"Deselect current chat"}
                                        >
                                            <CloseOutlined
                                                onClick={deselectChat}
                                                style={{ fontSize: "20px", cursor: "pointer" }}
                                            />
                                        </Tooltip>
                                        <Tooltip placement="topLeft" title={"Reload history"}>
                                            <ReloadOutlined
                                                onClick={() => reloadHistory(appState.selectedChat)}
                                                style={{
                                                    fontSize: "20px",
                                                    cursor: "pointer",
                                                    marginLeft: "10px",
                                                }}
                                            />
                                        </Tooltip>
                                    </div>
                                )}
                            </div>
                        }
                        onBack={isMobile && (() => closeChatCb())}
                        extra={
                            isMobile && (
                                <ChatHandlerButtonsNative
                                    handlers={{
                                        handleChatOptions: handleChatOptions,
                                        handleNote: handleNote,
                                        handleDeselectChat: deselectChat,
                                        handleReloadHistory: reloadHistory,
                                        handleOpenInfo: openInfoCb,
                                        handleOpenPredictModal: handleOpenPredictModal,
                                    }}
                                    showAssignButton={showAssignButton}
                                />
                            )
                        }
                    />
                </header>
                <div
                    style={{
                        position: "relative",
                        width: "100%",
                        overflow: "hidden",
                        height: `${
                            height - getHeaderHeight() - ionSafeAreasPx.bottom - ionSafeAreasPx.top
                        }px`,
                    }}
                >
                    <Chat
                        key={appState.selectedChat.id}
                        textInput={textInputValue}
                        handleTextInput={handleTextInputChange}
                        isFullScreen={false}
                        isLoading={false}
                        loadingCb={() => {}}
                        defaultAvatar={null}
                        isConnected={isConnected}
                        isEnabled={isChatEnabled}
                        inputPlaceholder={inputPlaceholder}
                        loadMoreText={t("loadMore")}
                        missingReferencedMessageText={t("missingReferencedMessage")}
                        customStyles={configs.customStyles}
                        language={lang}
                        sendIcon={SendIcon}
                        messages={messages}
                        paginator={paginator}
                        newMessageCb={handleNewClientMessage}
                        interactionCb={handleInteraction}
                        uploadFileCb={handleFileUpload}
                        newMultimediaMessageCb={handleMultimediaPreview}
                        loadMoreCb={onClickLoadMore}
                        scrollOnNewMessages={scrollOnNewMessages}
                        mapKey={""}
                        currentUserType={CTA.CHATS.USER_TYPE.AGENT}
                        showAudioRecordButton={showAudioRecorderButton}
                        referencedClickCb={handleReferencedClick}
                        chatHeight={height - getHeaderHeight() + "px"}
                        enableSendMultimediaButton={showSendMultimediaButton}
                    />
                </div>
                {renderAssignButton()}
                <CharSizeWarning text={textInputValue} chatChannel={chatChannel} />
            </section>
        );
    } else {
        return deepLinkChatState && deepLinkChatState.set ? (
            <ChatNotFound
                isActiveView={active}
                className={className}
                chatId={deepLinkChatState?.chatId}
            />
        ) : (
            <EmptyChat
                className={className}
                isActiveView={active}
                closeChatCb={closeChatCb}
                logoutCb={logout}
            />
        );
    }
}
export default ChatHandler;
